import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import React from 'react';
import get from 'lodash/get';
import find from 'lodash/find';
import { injectIntl, intlShape } from 'react-intl';
import * as utils from 'globals/utils';

import { validateEmail, validateNumber, validatePhone } from 'globals/validation';

import { getTeams, routeToConversation } from '../../app/ChatApp/actions';
import {
  selectIsAfterHours,
  selectOfflineImageUrl,
  selectTeamOptions,
  selectArticlesFetching,
  selectArticlesList,
} from '../../app/ChatApp/selectors';

import ChatMessage from '../../conversation/messages/ChatMessage';
import ChatFormInput from '../ChatFormInput';
import ChatAppOfflineWarning from '../../app/ChatAppOfflineWarning';
import KbArticle from '../../article/Article';

import * as helpers from './helpers';
import styles, { afterHoursStyles } from './styles';
import messagesIntl from './messages';

export const Message = data => {
  const sentBy = {};
  sentBy.type = data.sentBy;
  if (data.sentBy === 'user') {
    sentBy.id = '__team';
  }
  return {
    id: data.id,
    body: data.body,
    input: data.input,
    inputAt: data.inputAt,
    type: data.type,
    prompt: data.prompt,
    promptAt: data.promptAt,
    property: data.property,
    values: data.values,
    sentBy,
    query: data.query,
    hasResults: data.hasResults,
    noResults: data.noResults,
    followUpPrompt: data.followUpPrompt,
  };
};

export class ChatFormThread extends React.PureComponent {
  state = {
    questions: this.props.questions,
    messages: [],
    responses: [],
    waiting: false,
    error: null,
    deflectionForm: {
      selectedArticle: null,
      status: null,
      query: null,
      running: false,
      deflectedAt: null,
    },
  };

  observer = undefined;
  scrollContainerRef = undefined;

  componentDidMount() {
    const { formPrefill, proactive } = this.props;
    this.init();

    if (window.IntersectionObserver) {
      this.observer = new IntersectionObserver(this.observerCallback, {
        root: this.scrollContainerRef && this.scrollContainerRef.current,
      });
    }

    if (formPrefill) {
      return this.handleResponse({ body: formPrefill });
    }

    if (proactive) {
      return this.handleResponse({ proactive });
    }

    return undefined;
  }

  componentDidUpdate(prevProps) {
    const { formPrefill } = this.props;
    const prevFormPrefill = get(prevProps, 'formPrefill');

    if (formPrefill && formPrefill !== prevFormPrefill) {
      this.handleResponse({ body: formPrefill });
    }
  }

  observerCallback = (entries, observer) => {
    const observedElement = get(entries, 0);

    if (observedElement && !observedElement.inView) {
      observedElement.target.scrollIntoView();
      observer.unobserve(observedElement.target);
    }
  };

  handleSelectedArticle = article => {
    this.setState(prevState => ({
      deflectionForm: {
        ...prevState.deflectionForm,
        selectedArticle: article,
      },
    }));
  };

  init = () => {
    const { questions, requestTeams } = this.props;
    const teamQuestion = find(questions, q => q.property === 'conversation_team');
    if (teamQuestion && teamQuestion.values.length) {
      const ids = teamQuestion.values.join(',');
      return requestTeams(ids);
    }
    return false;
  };

  handleNextMessage = () => {
    const { delay } = this.props.delay;
    const { questions } = this.state;

    this.setState({ waiting: true });

    const currentQ = get(questions, 0);
    const currentPrompt = get(currentQ, 'prompt');
    const artificialDelay = helpers.calcDelayTime(currentPrompt, 80, delay);
    const currentQuestionType = get(currentQ, 'type');

    if (currentQuestionType === 'kbDeflect' || currentQuestionType === 'articleList') {
      this.setState(prevState => ({
        deflectionForm: { ...prevState.deflectionForm, running: true },
      }));
    }

    return setTimeout(() => {
      this.setState(
        prevState => {
          const { deflectionForm } = this.state;
          const clonedQuestions = [...prevState.questions];
          const question = clonedQuestions.shift();
          const clonedMsgs = [...prevState.messages];
          const now = new Date();
          const promptAt = now.toISOString();

          if (question && deflectionForm.status !== 'accepted') {
            const message = new Message({
              id: question.id,
              body: question.prompt,
              prompt: question.prompt,
              promptAt,
              sentBy: 'user',
              property: question.property,
              type: question.type,
              values: question.values,
            });

            clonedMsgs.push(message);
          }

          return {
            questions: clonedQuestions,
            messages: clonedMsgs,
          };
        },
        () => {
          const { messages, deflectionForm } = this.state;
          const lastMsg = messages[messages.length - 1];

          if (questions.length === 0 || deflectionForm.status === 'accepted') {
            return this.submit();
          }

          if (deflectionForm.running) {
            return this.setState({ waiting: false });
          }

          if (lastMsg.type === 'message') {
            return this.handleNextMessage();
          }

          return this.setState({ waiting: false });
        },
      );
    }, artificialDelay);
  };

  handleResponse = ({ body, value, displayName, proactive, endChat, skip }) => {
    const now = new Date();
    const inputAt = now.toISOString();

    if (value || skip || endChat) {
      this.setState(prevState => {
        const messages = prevState.messages;
        const prevDeflectionForm = prevState.deflectionForm;
        const responses = [...prevState.responses];
        const currentQ = get(messages, [messages.length - 1]);
        const currentQId = currentQ.id;
        const property = currentQ.property;

        const message = new Message({
          property,
          body: displayName,
          sentBy: 'customer',
          id: currentQId,
          input: displayName,
          value,
        });

        responses.push({ value, id: currentQ.id, inputAt, input: displayName });
        messages.push(message);

        if (prevDeflectionForm.running) {
          let status;

          if (endChat) {
            status = 'accepted';
          } else if (skip) {
            status = 'no-match';
          } else {
            status = 'rejected';
          }

          return {
            responses,
            messages,
            deflectionForm: {
              ...prevState.deflectionForm,
              status,
              deflectedAt: new Date().toISOString(),
              running: false,
            },
          };
        }

        return {
          responses,
          messages,
        };
      }, this.handleNextMessage);
      return;
    }

    const error = this.hasErrorMessage(body);

    if (error) {
      this.setState({ error });
      return;
    }

    this.setState(
      prevState => {
        const clonedQ = [...prevState.messages];
        const responses = [...prevState.responses];
        const lastQ = clonedQ[clonedQ.length - 1];
        const lasQuestionType = get(lastQ, 'type');
        const prevDeflectionForm = prevState.deflectionForm;

        if (lastQ) {
          responses.push({ input: body, id: lastQ.id, inputAt });
        }

        if (!proactive) {
          const message = new Message({ body, inputAt, sentBy: 'customer', id: lastQ ? lastQ.id : null });
          clonedQ.push(message);
        }

        if (lasQuestionType === 'kbDeflect') {
          const questions = this.props.questions;
          const question = questions.find(quest => quest.id === lastQ.id);

          const kbDeflectQuestionResponses = question && question.inputResponses;
          const followUpPrompt = question && question.followUpPrompt;

          const message1 = new Message({
            type: 'articleList',
            sentBy: 'user',
            query: body,
            followUpPrompt,
            hasResults: kbDeflectQuestionResponses.find(response => response.hasResults === true),
            noResults: kbDeflectQuestionResponses.find(response => response.hasResults === false),
            values: lastQ.values,
          });

          clonedQ.push(message1);
        }

        if (prevDeflectionForm.running) {
          return {
            error: null,
            messages: clonedQ,
            responses,
            deflectionForm: { ...prevState.deflectionForm, query: body },
          };
        }

        return {
          error: null,
          messages: clonedQ,
          responses,
        };
      },
      () => {
        const { questions: stateQuestions, deflectionForm } = this.state;
        const { questions: propsQuestions } = this.props;
        const isFirstQuestion = stateQuestions.length === propsQuestions.length;

        if (this.state.messages.length === 1 || (isFirstQuestion && proactive)) this.props.handleOnStart();

        if (deflectionForm.running) return;

        this.handleNextMessage();
      },
    );
  };

  submit = async () => {
    const { intl } = this.props;
    const { deflectionForm } = this.state;

    const deflectionData = {
      status: deflectionForm.status,
      query: deflectionForm.query,
      deflectedAt: deflectionForm.deflectedAt,
    };

    try {
      await this.props.onSubmit(this.state.messages, this.state.responses, deflectionData);

      return this.props.routeToConversation();
    } catch (e) {
      const body = intl.formatMessage(messagesIntl.submit_error);
      const error = new Message({ sentBy: 'user', body });
      this.setState({
        questions: [],
        messages: [error],
        responses: [],
        waiting: false,
        error: null,
      });
    }
    return false;
  };

  hasErrorMessage = body => {
    const { intl } = this.props;
    const { messages } = this.state;
    if (!messages.length || !body) return '';
    const lastQ = messages[messages.length - 1];
    if (!lastQ) return '';
    const property = get(lastQ, 'property');
    if (property === 'customer_phone' && !validatePhone(body)) {
      return intl.formatMessage(messagesIntl.placeholder_error_phone);
    }
    if (helpers.isCustomNumber(property) && !validateNumber(body)) {
      return intl.formatMessage(messagesIntl.placeholder_error_number);
    }
    if (property === 'customer_email' && !validateEmail(body)) {
      return intl.formatMessage(messagesIntl.placeholder_error_email);
    }
    return '';
  };

  renderMessages = () => {
    const { messages } = this.state;

    return messages.map((msg, i) => {
      const lastMessage = i === messages.length - 1;
      return (
        <div
          ref={ref => {
            if (lastMessage && ref && window.IntersectionObserver) {
              this.observer.observe(ref);
            }
          }}
          key={i}
        >
          {this.renderFormMessage(msg)}
          {this.renderCustomerMessage(msg)}
        </div>
      );
    });
  };

  renderFormMessage = msg => {
    if (!get(msg, 'sentBy.type') || msg.sentBy.type === 'customer') return null;
    return (
      <ChatMessage message={msg} onSelectArticle={this.handleSelectedArticle} onHandleResponse={this.handleResponse} />
    );
  };

  renderCustomerMessage = msg => {
    if (!get(msg, 'sentBy.type') || msg.sentBy.type === 'user') return null;
    return <ChatMessage message={msg} />;
  };

  renderChatFormInput() {
    const { teams, isAfterHours, articles, fetchingArticles } = this.props;
    const { messages, waiting, error } = this.state;
    const current = messages[messages.length - 1];
    const property = get(current, 'property', '');
    const isMLL = property.includes('Tree');
    const isArticleList = get(current, 'type') === 'articleList';

    let values = null;

    if (get(current, 'values') && get(current, 'type') !== 'kbDeflect') {
      const isTeam = get(current, 'property') === 'conversation_team';
      values = isTeam ? [...teams] : current.values.map(value => ({ displayName: value, value }));

      if (isArticleList) {
        values = current.values.map(value => ({
          displayName: value.displayName,
          value: value.displayName,
          endChat: value.endChat,
        }));
      }
    }

    if (isArticleList && (fetchingArticles || (articles && articles.length < 1))) {
      return null;
    }

    return (
      <ChatFormInput
        onSubmit={this.handleResponse}
        values={values}
        disabled={isArticleList ? true : waiting}
        error={error}
        isAfterHours={isAfterHours}
        isMLL={isMLL}
        property={property}
      />
    );
  }
  render() {
    const { isAfterHours, offhoursImageUrl, intl } = this.props;
    const { messages, deflectionForm } = this.state;

    const scrollContainerStyle = utils.isMobile() ? styles.mobileScrollContainer : styles.scrollContainer;
    let messageContainerStyle = utils.isMobile() ? styles.mobileMessageContainer : styles.messagesContainer;

    const articleContainerStyles = deflectionForm.selectedArticle
      ? styles.articleContainer
      : { ...styles.articleContainer, ...styles.articleContainerClosed };

    if (deflectionForm.selectedArticle) {
      messageContainerStyle = { ...messageContainerStyle, display: 'none' };
    }

    return (
      <div style={styles.container}>
        {deflectionForm.selectedArticle ? (
          <div style={styles.iconContainer} onClick={() => this.handleSelectedArticle(null)}>
            <div style={styles.collapseIcon} />
          </div>
        ) : null}
        <div
          style={isAfterHours && !messages.length ? afterHoursStyles(offhoursImageUrl) : scrollContainerStyle}
          ref={this.scrollContainerRef}
          className="kustomer-app-thread"
        >
          <div style={messageContainerStyle}>{this.renderMessages()}</div>
          <div style={articleContainerStyles}>
            <KbArticle article={deflectionForm.selectedArticle} />
          </div>
        </div>
        {deflectionForm.selectedArticle ? null : (
          <div style={utils.isMobile() ? styles.mobileInputContainer : null}>
            <ChatAppOfflineWarning />
            {deflectionForm.status === 'accepted' ? (
              <div style={styles.wrapperDisabled}>
                <span style={styles.disabledText}>{intl.formatMessage(messagesIntl.thank_you_message)}</span>
              </div>
            ) : (
              this.renderChatFormInput()
            )}
          </div>
        )}
      </div>
    );
  }
}

ChatFormThread.propTypes = {
  formPrefill: PropTypes.string,
  questions: PropTypes.arrayOf(PropTypes.object),
  delay: PropTypes.number,
  onSubmit: PropTypes.func,
  handleOnStart: PropTypes.func,
  requestTeams: PropTypes.func,
  routeToConversation: PropTypes.func,
  teams: PropTypes.arrayOf(PropTypes.object),
  isAfterHours: PropTypes.bool,
  offhoursImageUrl: PropTypes.string,
  intl: intlShape.isRequired,
  proactive: PropTypes.bool,
  articles: PropTypes.instanceOf(Array),
  fetchingArticles: PropTypes.bool,
};

ChatFormThread.defaultProps = {
  formPrefill: null,
  questions: [],
  delay: 2000,
  teams: [],
  isAfterHours: false,
  offhoursImageUrl: '',
  handleOnStart: () => {},
  proactive: false,
  articles: undefined,
  fetchingArticles: false,
};

const mapStateToProps = state => {
  return {
    isAfterHours: selectIsAfterHours(state),
    offhoursImageUrl: selectOfflineImageUrl(state),
    teams: selectTeamOptions(state),
    articles: selectArticlesList(state),
    fetching: selectArticlesFetching(state),
  };
};

const mapDispatchToProps = dispatch => {
  return {
    requestTeams(ids) {
      return dispatch(getTeams(ids));
    },
    routeToConversation() {
      return dispatch(routeToConversation());
    },
  };
};

export const intlComponent = injectIntl(ChatFormThread, { withRef: true });

const connectedChatFormThread = connect(
  mapStateToProps,
  mapDispatchToProps,
)(intlComponent);

export default connectedChatFormThread;
